import classNames from 'classnames';
import { useRef, useState } from 'react';
import styled from 'styled-components';

import { AnimatedNumber } from '#src/atoms/animations/AnimatedNumber/index.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { LabelS } from '#src/atoms/typography/index.tsx';
import type { CurrencyData } from '#src/components/navigation/AppNavigation/UserCurrencies/useUserCurrencies.tsx';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { useWaitForElementVisible } from '#src/hooks/useWaitForElementVisible.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

import { CurrencyAnimation } from './CurrencyAnimation.tsx';

const InGameCurrencyWrapper = styled(Horizontal).attrs({
  gap: 0.5,
  center: true,
})`
  background-color: var(--c-black);
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: 50%;
  border: 1px solid var(--c-nd-150);
  padding: 0 var(--unit);
  .sidebarCollapsed & {
    ${LabelS} {
      --typography-font: var(--t-label-xs);
    }
  }

  .enableSeriesNavigationUpdate & {
    background-color: var(--c-nd-50);
    border: none;
    backdrop-filter: blur(2px);
    @media ${laptopAndAbove} {
      background-color: var(--c-black);
      border: 1px solid var(--c-nd-150);
      backdrop-filter: none;
    }
  }

  &.expanded {
    border-radius: var(--double-unit);
    width: auto;
    min-width: 57px;
  }

  &.selected {
    background-color: var(--c-nd-50);
    border-color: var(--c-nd-300);
  }
  transition:
    300ms background-color ease-in-out,
    300ms border-color ease-in-out;

  &.vertical {
    background-color: transparent;
    padding: 0 var(--half-unit);
    border: none;
    flex-direction: column;
    width: auto;
    height: auto;
    min-width: var(--quadruple-unit);
  }

  @media ${laptopAndAbove} {
    flex-direction: column;
    justify-content: center;

    flex: 1;
    padding: var(--unit);
    border-radius: var(--unit);
    height: auto;
  }
`;

type Props = {
  currency: CurrencyData;
  selected: boolean;
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
  style?: React.CSSProperties;
  variant?: 'vertical' | 'horizontal';
};

export const Currency = ({
  currency,
  onMouseEnter,
  style,
  selected,
  variant,
}: Props) => {
  const { sport } = useSportContext();
  const isLaptopAndAbove = useIsLaptop();
  const {
    icon,
    expanded: expandedFromProps,
    visibleOnSports,
    amount,
    onClick,
  } = currency;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [displayedAmount, setDisplayedAmount] = useState<number>(amount ?? 0);
  const [animationDelta, setAnimationDelta] = useState<number>(0);
  const expanded = variant === 'vertical' ? true : expandedFromProps;

  const pendingAmount =
    typeof amount === 'number' && amount !== displayedAmount ? amount : null;

  const handleVisible = () => {
    if (pendingAmount !== null) {
      const delta = pendingAmount - displayedAmount;
      if (delta > 0) {
        setAnimationDelta(delta);
      }
      setDisplayedAmount(pendingAmount);
    }
  };

  useWaitForElementVisible(wrapperRef, handleVisible, pendingAmount !== null);

  if (!visibleOnSports.includes(sport) || typeof amount !== 'number') {
    return null;
  }

  return (
    <>
      <InGameCurrencyWrapper
        ref={wrapperRef}
        as={isLaptopAndAbove ? 'button' : 'div'}
        onClick={isLaptopAndAbove ? onClick : undefined}
        className={classNames(variant, {
          expanded: !isLaptopAndAbove && expanded,
          selected,
        })}
        style={style}
        onMouseEnter={onMouseEnter}
      >
        {icon}
        {!!(isLaptopAndAbove || expanded) && (
          <LabelS bold>
            <AnimatedNumber
              value={displayedAmount}
              disableAnimationOnFirstRender
              formatNumberProps={{
                notation: 'compact',
                maximumFractionDigits: 1,
                compactDisplay: 'short',
              }}
              animationProps={{
                config: { duration: 2000 },
              }}
            />
          </LabelS>
        )}
      </InGameCurrencyWrapper>
      <CurrencyAnimation
        key={currency.id}
        icon={icon}
        count={animationDelta}
        onAnimationComplete={() => {
          setAnimationDelta(0);
        }}
        targetRef={wrapperRef}
      />
    </>
  );
};
